import { Fragment } from 'react'

import {
  Card,
  Divider,
  IconExternalLink,
  IconPointerRight,
  PrimaryButton,
  Space,
  Text,
} from '@procurify/ui'

import { Price } from '@webapp/core/components/Price'
import { type CurrencyName } from '@webapp/core/entities/external/Currency/CurrencyName'
import { useProcurifyIntl } from '@webapp/hooks'
import { ConversationVendorOrderSummaryHeading } from './ConversationVendorOrderSummaryHeading'
import { ConversationVendorOrderSummaryLine } from './ConversationVendorOrderSummaryLine'
import messages from './messages'
import { ConversationOrderSummaryCardStyled } from './styles/ConversationOrderSummaryCardStyled'
import { ConversationOrderSummaryDividerStyled } from './styles/ConversationOrderSummaryDividerStyled'
import { ConversationOrderSummaryFullRowStyled } from './styles/ConversationOrderSummaryFullRowStyled'
import { ConversationOrderSummaryLinesStyled } from './styles/ConversationOrderSummaryLinesStyled'
import {
  type ConversationCartAction,
  type ConversationCartAmount,
  type ConversationVendor,
} from '../../value-objects/ConversationTurnItem'
import { ConversationCartActionIcons } from '../../value-objects/enums/ConversationCartActionIconEnum'

export interface IConversationVendorOrderSummaryItem {
  id: string
  name: string
  img?: string
  quantity: number
  price: number
  currency?: CurrencyName
}

export interface IConversationVendorOrderSummaryGroup {
  vendor?: ConversationVendor
  items: IConversationVendorOrderSummaryItem[]
  subtotal?: ConversationCartAmount
}

export interface IConversationVendorOrderSummaryProps {
  groups: IConversationVendorOrderSummaryGroup[]
  total: Nullable<number>
  currency: CurrencyName
  disabled?: boolean
  isContinueLoading?: boolean
  isRemoveDisabled?: boolean
  totalLabel?: string
  continueAction?: ConversationCartAction
  disclaimer?: string
  onQuantityChange: (itemId: string, quantity: number) => void
  onRemove: (itemId: string) => void
  onContinue: () => void
}

export const ConversationVendorOrderSummary = ({
  groups,
  total,
  currency,
  disabled,
  isContinueLoading,
  isRemoveDisabled,
  totalLabel,
  continueAction,
  disclaimer,
  onQuantityChange,
  onRemove,
  onContinue,
}: IConversationVendorOrderSummaryProps) => {
  const intl = useProcurifyIntl()

  const renderGroups = () => (
    <Card.Content>
      <ConversationOrderSummaryLinesStyled>
        {groups.map((group, index) => (
          <Fragment key={group.vendor?.id ?? index}>
            {index > 0 && (
              <ConversationOrderSummaryFullRowStyled $isDivider>
                <Divider space={0} />
              </ConversationOrderSummaryFullRowStyled>
            )}
            <ConversationOrderSummaryFullRowStyled>
              <ConversationVendorOrderSummaryHeading group={group} />
            </ConversationOrderSummaryFullRowStyled>
            {group.items.map((item) => (
              <ConversationVendorOrderSummaryLine
                key={item.id}
                item={item}
                currency={currency}
                disabled={disabled}
                isRemoveDisabled={isRemoveDisabled}
                onQuantityChange={onQuantityChange}
                onRemove={onRemove}
              />
            ))}
          </Fragment>
        ))}
      </ConversationOrderSummaryLinesStyled>
    </Card.Content>
  )

  const renderAmountRow = (
    key: string,
    label: string,
    amount: ConversationCartAmount
  ) => (
    <Space
      key={key}
      direction='horizontal'
      justify='space-between'
      align='center'
    >
      <Text inline bold>
        {label}
      </Text>
      <Text inline bold className='fs-mask'>
        <Price inline price={amount.price} currency={amount.currency} />
      </Text>
    </Space>
  )

  const renderTotals = () => {
    if (total !== null)
      return renderAmountRow(
        'total',
        totalLabel ?? intl.formatMessage(messages.estimatedTotal),
        { price: total, currency }
      )
    return groups.map((group, index) =>
      group.subtotal
        ? renderAmountRow(
            group.vendor?.id ?? String(index),
            intl.formatMessage(messages.vendorSubtotal, {
              vendor: group.vendor?.name ?? '',
            }),
            group.subtotal
          )
        : null
    )
  }

  return (
    <ConversationOrderSummaryCardStyled>
      {renderGroups()}

      <ConversationOrderSummaryDividerStyled>
        <Divider space={0} />
      </ConversationOrderSummaryDividerStyled>

      <Card.Footer>
        <Space direction='vertical' size='medium'>
          {renderTotals()}

          <PrimaryButton
            analyticsId='conversation-continue-to-request-button'
            showIconOnEnd
            iconComponent={
              continueAction?.icon === ConversationCartActionIcons.EXTERNAL ? (
                <IconExternalLink />
              ) : (
                <IconPointerRight />
              )
            }
            disabled={disabled}
            isLoading={isContinueLoading}
            onClick={onContinue}
          >
            {continueAction?.text ??
              intl.formatMessage(messages.continueButton)}
          </PrimaryButton>

          <Text color='textSecondary'>
            {disclaimer ?? intl.formatMessage(messages.disclaimer)}
          </Text>
        </Space>
      </Card.Footer>
    </ConversationOrderSummaryCardStyled>
  )
}
